import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import Photo from "../components/Photo";
import Reveal from "../components/Reveal";
import { Arrow } from "../components/Icons";
import { SectionHeading, Icon, Counter, FAQ, CTABand, faqJsonLd } from "../components/Shared";
import { services, process, stats } from "../data/services";
import { faqs } from "../data/content";
import { posts } from "../data/posts";

export default function Home() {
  useSEO({
    description: "Victrie is a next-generation software development company in Nairobi, Kenya. We build custom software, AI solutions, web and mobile apps and cloud platforms for businesses across Africa and the world.",
    path: "/",
    jsonLd: faqJsonLd(faqs.slice(0, 5)),
  });

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="container hero__inner">
          <div>
            <Reveal>
              <span className="hero__badge">
                <span className="tag" style={{ background: "var(--grad)", color: "#05070f", border: 0, fontWeight: 700 }}>NEW</span>
                AI-powered product studio<i />
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1>
                We engineer <span className="grad-text">futuristic software</span> for the next generation of business.
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="lead">
                Victrie is a Nairobi-based software development company building AI solutions, custom web &amp; mobile applications and cloud platforms that put your business years ahead of the competition.
              </p>
            </Reveal>
            <Reveal delay={240} className="hero__actions">
              <Link to="/contact" className="btn btn--primary btn--lg">Start your project <Arrow /></Link>
              <Link to="/solutions" className="btn btn--ghost btn--lg">Upcoming solutions</Link>
            </Reveal>
          </div>
          <Reveal delay={200} className="hero__visual">
            <Photo image="couple-project" eager alt="Victrie engineers collaborating in the Nairobi office"
            />
            <div className="hero__chip glass hero__chip--1"><span className="dot" />AI &amp; Machine Learning</div>
            <div className="hero__chip glass hero__chip--2"><span className="dot" />Cloud-native · 99.99% uptime</div>
            <div className="hero__chip glass hero__chip--3"><span className="dot" />Web · Mobile · Data</div>
          </Reveal>
        </div>
        <div className="scroll-hint">Scroll</div>
      </section>

      {/* SERVICES */}
      <section className="section" id="services">
        <div className="container">
          <SectionHeading
            eyebrow="What we do"
            title="Next-generation solutions, engineered end to end"
            text="From strategy and design to AI, engineering and cloud operations, Victrie is the single partner that takes your product from idea to global scale."
          />
          <div className="grid grid--3">
            {services.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 70} className="card service-card">
                <div className="card__glow" />
                <div className="icon-badge"><Icon name={s.icon} /></div>
                <h3>{s.title}</h3>
                <p>{s.short}</p>
                <ul>{s.tags.slice(0, 3).map((t) => <li key={t}>{t}</li>)}</ul>
                <Link to={`/services#${s.slug}`} className="link-arrow">Learn more <Arrow /></Link>
              </Reveal>
            ))}
          </div>
          <Reveal style={{ textAlign: "center", marginTop: 40 }}>
            <Link to="/services" className="btn btn--ghost">View all services <Arrow /></Link>
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container split">
          <Reveal>
            <Photo image="team-project" alt="The Victrie team working together on a product"
              hover
              style={{ aspectRatio: "1/1" }}
            />
          </Reveal>
          <Reveal delay={120}>
            <span className="eyebrow">Why Victrie</span>
            <h2>Built for the world that is coming, not the one that was</h2>
            <p className="lead">Most software agencies build what you ask for. We build what your business will need in three years, and make it work today.</p>
            <ul className="checklist">
              <li><span><strong>Senior-only engineering teams.</strong> No juniors learning on your budget.</span></li>
              <li><span><strong>AI-native from the ground up.</strong> Intelligence designed into every product, not bolted on.</span></li>
              <li><span><strong>Transparent, fixed-scope pricing.</strong> Weekly demos, no surprises, you own every line of code.</span></li>
              <li><span><strong>Security and compliance built in.</strong> GDPR, Kenya DPA and industry standards from day one.</span></li>
            </ul>
            <Link to="/about" className="btn btn--primary">Our story <Arrow /></Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="section--tight">
        <div className="container">
          <div className="stats">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80} className="glass stat">
                <Counter value={s.value} suffix={s.suffix} />
                <span>{s.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* PROCESS */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container split">
          <Reveal>
            <span className="eyebrow">How we work</span>
            <h2>A process engineered for certainty</h2>
            <p className="lead">Five phases, weekly demos and complete transparency. You always know where your product is, and where it is going.</p>
            <Link to="/contact" className="btn btn--primary" style={{ marginTop: 12 }}>Book a discovery call <Arrow /></Link>
          </Reveal>
          <div className="process">
            {process.map((p, i) => (
              <Reveal key={p.title} delay={i * 80} className="process__item">
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* INSIGHTS */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="Insights" title="Ideas shaping the future of technology" text="Practical thinking on AI, software engineering, cloud and product design from the Victrie team." />
          <div className="grid grid--3">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 90} className="card post-card" as="article">
                <Link to={`/insights/${p.slug}`}>
                  <Photo image={p.image} alt={p.title} hover />
                </Link>
                <div className="post-card__body">
                  <div className="post-card__meta"><b>{p.category}</b><span>{p.readTime} min read</span></div>
                  <h3><Link to={`/insights/${p.slug}`}>{p.title}</Link></h3>
                  <p>{p.excerpt}</p>
                  <Link to={`/insights/${p.slug}`} className="link-arrow">Read article <Arrow /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="FAQ" title="Questions, answered" center />
          <FAQ items={faqs.slice(0, 5)} />
        </div>
      </section>

      <CTABand />
    </>
  );
}
